import Add from "@mui/icons-material/Add";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import OpenInNew from "@mui/icons-material/OpenInNew";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GetWasteShipmentQuery, Issue, IssueLevel } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { Link as RouterLink } from "react-router-dom";

import { containerTypeLabel } from "../Containers/containerDisplay";
import {
  containerShipOutcome,
  formatQuantity,
  formatShipmentDate,
  formatTotalsByUnit,
  groupLinesByStream,
  totalsByUnitFor,
} from "./shipmentDisplay";
import { DisplayLine } from "./useStagedShipmentLines";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;

/**
 * The container rows are a flex layout rather than a table, so the column
 * header and the rows have to agree on widths independently — these keep them
 * from drifting. The actions column is pinned because it holds either two icon
 * buttons or an Undo button; without a fixed width the header's quantity label
 * would sit off to the right of the amounts it names.
 */
const QUANTITY_COLUMN = { minWidth: 90, textAlign: "right" as const };
const ACTIONS_COLUMN = {
  width: 72,
  flexShrink: 0,
  display: "flex",
  justifyContent: "flex-end",
};

/**
 * A shipment's contents, grouped by waste stream.
 *
 * The stream is the unit the vendor sees — the manifest request rolls up to it
 * — so it is the group header here too, carrying the rollup (container count,
 * per-unit total, codes, DOT, profile). Its containers sit underneath with
 * their own context. That way the internal view and the vendor's copy of the
 * request describe the same thing, and a problem with one container has an
 * obvious place to live: on its own row, beside the control that fixes it.
 */
export function ShipmentContentsCard({
  shipment,
  lines,
  editable,
  issues,
  onAddContainers,
  onEditLine,
  onRemoveLine,
  onRestoreLine,
}: {
  shipment: Shipment;
  /** Saved lines plus anything staged; see useStagedShipmentLines. */
  lines: DisplayLine[];
  editable: boolean;
  /** Standing issues for the shipment; per-container ones carry a modelId. */
  issues: Issue[];
  onAddContainers: () => void;
  onEditLine: (line: DisplayLine) => void;
  onRemoveLine: (line: DisplayLine) => void;
  onRestoreLine: (line: DisplayLine) => void;
}) {
  const { tenantId } = useTenant();
  // Staged removals stay visible, struck through, so Discard is meaningful —
  // but the rows are what stays visible, not the tallies. Every figure in the
  // subheader is computed from the same staged-aware set, so it can't report an
  // edited container count beside an unedited stream count and saved total.
  const shipped = !!shipment.shippedDate;
  const counted = lines.filter((line) => line.pending !== "removed");
  const groups = groupLinesByStream(lines);
  const countedGroups = groupLinesByStream(counted);
  const countedTotals = totalsByUnitFor(counted);

  const issuesForContainer = (containerId: string) =>
    issues.filter((issue) => issue.modelId === containerId);
  // Anything not pinned to a container belongs to the shipment as a whole.
  const shipmentIssues = issues.filter((issue) => !issue.modelId);

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
        spacing={2}
      >
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Typography variant="h6">Contents</Typography>
          <Typography variant="body2" color="text.secondary">
            {counted.length} {counted.length === 1 ? "container" : "containers"} ·{" "}
            {countedGroups.length} {countedGroups.length === 1 ? "stream" : "streams"} ·{" "}
            {formatTotalsByUnit(countedTotals)}
          </Typography>
        </Stack>
        {editable && (
          <Button size="small" startIcon={<Add />} onClick={onAddContainers}>
            Add containers
          </Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {shipped
          ? `Shipped ${formatShipmentDate(shipment.shippedDate)}. Grouped by waste stream, as on the manifest request.`
          : "Grouped by waste stream — this is how the vendor sees it on the manifest request, which is not an official manifest."}
      </Typography>

      {shipmentIssues.map((issue, i) => (
        <Alert
          key={`${issue.key}-${i}`}
          severity={issue.level === IssueLevel.Critical ? "error" : "warning"}
          sx={{ mb: 2 }}
        >
          {issue.message}
        </Alert>
      ))}

      {lines.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center", backgroundColor: "grey.50", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No containers on this shipment yet.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {/* Labels the container rows' columns. The stream headers below name
              the stream and carry its rollup, so this sits above all of them
              rather than repeating per group. The amount column is what a
              shipment is actually about, and it read as a bare number before. */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
              Container
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={QUANTITY_COLUMN}>
              {shipped ? "Shipped" : "Shipping"}
            </Typography>
            {editable && <Box sx={ACTIONS_COLUMN} />}
          </Stack>
          {groups.map((group) => {
            const groupIssueCount = group.lines.reduce(
              (n, line) => n + issuesForContainer(line.container.id).length,
              0
            );
            const groupBlocked = group.lines.some((line) =>
              issuesForContainer(line.container.id).some(
                (issue) => issue.level === IssueLevel.Critical
              )
            );
            return (
              <Box key={group.key}>
                {/* Stream header = the manifest-request line for this stream. */}
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ pb: 0.5, borderBottom: 1, borderColor: "divider" }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">
                      {group.streamName ?? "Uncharacterized"}
                    </Typography>
                    {groupIssueCount > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={groupBlocked ? "error" : "warning"}
                        label={`${groupIssueCount} ${groupIssueCount === 1 ? "issue" : "issues"}`}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {group.lines.length} {group.lines.length === 1 ? "container" : "containers"} ·{" "}
                    {formatTotalsByUnit(group.totals)}
                  </Typography>
                </Stack>

                <Typography variant="caption" color="text.secondary" display="block" sx={{ py: 1 }}>
                  {[
                    [...group.federalWasteCodes, ...group.stateWasteCodes].join(", ") || null,
                    group.dotDescription,
                    group.profileNumber ? `Profile ${group.profileNumber}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "No codes or DOT description recorded"}
                </Typography>

                <Stack>
                  {group.lines.map((line) => (
                    <ContainerRow
                      key={line.key}
                      line={line}
                      tenantId={tenantId}
                      editable={editable}
                      shipped={shipped}
                      issues={issuesForContainer(line.container.id)}
                      onEdit={() => onEditLine(line)}
                      onRemove={() => onRemoveLine(line)}
                      onRestore={() => onRestoreLine(line)}
                    />
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}

function ContainerRow({
  line,
  tenantId,
  editable,
  shipped,
  issues,
  onEdit,
  onRemove,
  onRestore,
}: {
  line: DisplayLine;
  tenantId: string | undefined;
  editable: boolean;
  shipped: boolean;
  issues: Issue[];
  onEdit: () => void;
  onRemove: () => void;
  onRestore: () => void;
}) {
  const { container } = line;
  const outcome = shipped ? containerShipOutcome(line) : null;
  const removed = line.pending === "removed";

  return (
    <Box
      sx={{
        py: 1,
        opacity: removed ? 0.5 : 1,
        "&:not(:last-of-type)": { borderBottom: 1, borderColor: "grey.100" },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* New tab, per the ModelLinks convention: the container is a
                side-trip from the shipment you are editing, not a destination. */}
            <Link
              component={RouterLink}
              to={`/o/${tenantId}/waste/containers/${container.id}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              {container.containerLabel}
              <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
            {container.isPermanent && <Chip label="Tank" size="small" variant="outlined" />}
            {line.pending === "added" && (
              <Chip label="Adding" size="small" color="info" variant="outlined" />
            )}
            {removed && <Chip label="Removing" size="small" variant="outlined" />}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {[
              containerTypeLabel(container.containerType),
              container.accumulationArea?.name,
              // Pre-ship, what's in the container is the useful context; the
              // shipped quantity is in its own column.
              shipped ? null : `${formatQuantity(container.amount, container.amountUnit)} on site`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Box>

        {shipped ? (
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="body2">
              {formatQuantity(line.quantity, line.unit)} shipped
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {outcome?.label}
              {outcome?.detail ? ` · ${outcome.detail}` : ""}
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              ...QUANTITY_COLUMN,
              textDecoration: removed ? "line-through" : undefined,
            }}
          >
            {formatQuantity(line.quantity, line.unit)}
          </Typography>
        )}

        {editable ? (
          <Stack direction="row" spacing={0.5} sx={ACTIONS_COLUMN}>
            {removed ? (
              <Button size="small" onClick={onRestore}>
                Undo
              </Button>
            ) : (
              <>
                <Tooltip
                  title={
                    line.lineId
                      ? "Edit quantity"
                      : "Save the shipment before shipping part of this container"
                  }
                >
                  {/* A staged container has no saved line to validate a
                      partial amount against, so this waits for the save. */}
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Edit ${container.containerLabel}`}
                      onClick={onEdit}
                      disabled={!line.lineId}
                    >
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove from shipment">
                  <IconButton
                    size="small"
                    aria-label={`Remove ${container.containerLabel}`}
                    onClick={onRemove}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        ) : (
          // Keeps the shipped rows' columns aligned with the editable ones.
          <Box sx={{ width: 68 }} />
        )}
      </Stack>

      {issues.map((issue, i) => (
        <Stack
          key={`${issue.key}-${i}`}
          direction="row"
          spacing={0.75}
          alignItems="flex-start"
          sx={{ pl: 0.25, pt: 0.5 }}
        >
          {issue.level === IssueLevel.Critical ? (
            <ErrorOutline color="error" sx={{ fontSize: 16, mt: "1px" }} />
          ) : (
            <WarningAmber color="warning" sx={{ fontSize: 16, mt: "1px" }} />
          )}
          <Typography
            variant="caption"
            color={issue.level === IssueLevel.Critical ? "error.main" : "warning.main"}
          >
            {issue.message}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}
